import { config } from '../config'
import { storage } from '../auth/storage'

export class HttpError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message)
  }
}

export class UnauthorizedError extends HttpError {
  constructor(body?: unknown) {
    super(401, 'Unauthorized', body)
  }
}

interface FetchOptions {
  method?: string
  query?: Record<string, string | number | undefined>
  body?: unknown
  signal?: AbortSignal
}

export async function apiFetch<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const url = new URL(`${config.facadeOrigin}${path}`)
  if (opts.query) {
    Object.entries(opts.query).forEach(([k, v]) => {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
    })
  }

  const headers: Record<string, string> = {}
  const token = storage.getAccessToken()
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(url.toString(), {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal
  })

  if (res.status === 401) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      // ignore
    }
    throw new UnauthorizedError(body)
  }

  if (!res.ok) {
    let body: unknown
    let text = ''
    try {
      text = await res.text()
      body = JSON.parse(text)
    } catch {
      body = text
    }
    throw new HttpError(res.status, `${res.status} ${res.statusText}`, body)
  }

  return (await res.json()) as T
}
